<template>
  <CoreDialog
    data-qa="search-modal"
    :value="value"
    size="fullscreen"
    @input="emit('input', $event)"
  >
    <template #extension>
      <v-form
        class="d-flex align-center"
        @submit.prevent="handleSubmitForm"
      >
        <v-text-field
          id="search-field"
          ref="textField"
          v-model="query"
          :maxlength="MAX_QUERY_LENGTH"
          class="search-dialog-input"
          type="search"
          :height="48"
          dense
          solo
          flat
          clear-icon="ui-icon-close-not-a-circle"
          :placeholder="`Поиск в ${selectedCity.name_pred}`"
          autofocus
          full-width
          hide-details
          clearable
          @focus="handleFocusSearchInput"
          @click:append-outer="handleSubmitForm"
          @click:prepend-inner="emit('input', false)"
          @click:clear="query = ''"
        >
          <template
            #prepend-inner
          >
            <v-img
              src="/static/_v1/pd/favicons/favicon_32x32.png"
              height="20"
              width="20"
              class="mr-1"
              contain
            />
          </template>
        </v-text-field>
        <v-btn
          small
          text
          color="primary"
          class="ml-2 px-3"
          @click="handleClickCancel"
        >
          Отмена
        </v-btn>
      </v-form>

      <div>
        <v-btn
          text
          small
          height="28"
          class="pl-2 pr-4"
          @click="isVisibleCityModal = true"
        >
          <v-icon
            size="20"
            class="mr-2"
            color="icon-secondary"
          >
            ui-icon-map-pin
          </v-icon>
          {{ selectedCity.name }}
        </v-btn>
        <CityModal
          v-model="isVisibleCityModal"
          :selected-city="selectedCity"
          @city:selected="onClickCity"
        />
      </div>
    </template>
    <template #default="{ xSpaceClass, counterXSpaceClass }">
      <SearchResults
        v-if="query?.length >= MIN_QUERY_LENGTH"
        :query="query"
        :town="selectedCity"
        :is-office-ip="context.is_office_ip"
        :class="counterXSpaceClass"
        :x-space-class="xSpaceClass"
      />
      <div
        v-else
        class="text-body-1 text-secondary--text"
      >
        <NavLinks
          :town="selectedCity"
          :counter-x-space-class="counterXSpaceClass"
          :x-space-class="xSpaceClass"
        />
      </div>
    </template>
  </CoreDialog>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref } from 'vue'
import { VTextField } from 'vuetify/lib'
import type { ContextStore } from 'src/layouts/core-layout/types'
import { useContextStore } from 'src/store'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import CityModal from 'src/layouts/core-layout/components/CoreSearch/components/CityModal.vue'
import NavLinks from 'src/layouts/core-layout/components/CoreSearch/components/NavLinks.vue'
import SearchResults from 'src/layouts/core-layout/components/CoreSearch/components/SearchResults.vue'
import { MAX_QUERY_LENGTH, MIN_QUERY_LENGTH } from 'src/layouts/core-layout/components/CoreSearch/constants'
import type { Location } from 'src/layouts/core-layout/components/CoreSearch/types'
import type { ApiCitiesResponseGet } from 'src/layouts/core-layout/components/CoreSearch/api/apiCities.types'
import buildSearchUrl from 'src/layouts/core-layout/components/CoreSearch/functions/buildSearchUrl'
import sendYaGoal from 'src/utils/sendYaGoal'
import redirect from 'src/utils/redirect'

defineProps<{
  value: boolean
}>()

const emit = defineEmits(['input'])

const { context }: ContextStore = useContextStore()

const town = {
  id: context.town.id,
  name: context.town.name,
  name_pred: context.town.name_pred,
  translit: context.town.translit,
}

// @ts-ignore
const location: Location | undefined = context.metro || context.district
  ? {
    type: context.metro ? 'metro' : 'district',
    name: context.metro ? context.metro.translit : context.district?.translit,
  }
  : undefined

const query = ref('')
const isVisibleCityModal = ref(false)
const selectedCity = ref(town)

const textField = ref<InstanceType<typeof VTextField> & {
  isFocused: boolean
  blur:() => void
}>()

const handleClickCancel = () => {
  emit('input', false)
  query.value = ''
  selectedCity.value = town
}

function blurOnTouchstart({ target }: Event) {
  if (!(target instanceof HTMLInputElement)) {
    return
  }

  if (target.getAttribute('id') === 'search-field') {
    return
  }

  if (textField.value?.isFocused) {
    textField.value.blur()
  }
}

function handleSubmitForm() {
  if (query.value) {
    if (!context.is_office_ip) {
      sendYaGoal('oldSearch_searchButton')
    }

    redirect({
      path: buildSearchUrl({
        query: query.value,
        searchUrl: `/${selectedCity.value.translit}/find/`,
        location,
      }),
    })
  }
}

function handleFocusSearchInput() {
  if (!context.is_office_ip) {
    sendYaGoal('search_focus')
  }
}

if (process.env.IS_CLIENT) {
  window.addEventListener('touchstart', blurOnTouchstart)

  onBeforeUnmount(() => {
    window.removeEventListener('touchstart', blurOnTouchstart)
  })
}

const triggerWatchQuery = async () => {
  const oldValue = query.value

  query.value = ''

  await nextTick()

  // eslint-disable-next-line require-atomic-updates
  query.value = oldValue
}

const onClickCity = (city: ApiCitiesResponseGet[number]) => {
  selectedCity.value = city
  isVisibleCityModal.value = false

  triggerWatchQuery()
}
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/variables';

.search-dialog-input {
  transition: box-shadow 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);

  &:hover {
    box-shadow: 0 0 0 1px $ui-kit-primary;
  }

  &.v-input--is-focused {
    box-shadow: 0 0 0 2px $ui-kit-primary;
    caret-color: $ui-kit-primary;
  }

  :deep(.v-input__slot) {
    background-color: $ui-kit-bg-gray-40 !important;
  }

  :deep(input)::placeholder {
    color: $ui-kit-text-info;
  }
}
</style>
